<template>
  <div
    class="audio-player-wrapper"
    :class="{
      'has-export-panel': isExportPanelVisible,
      'has-learning-assistant': learningAssistant.open,
      'has-video': hasVisualMedia,
    }"
    @click="closeGenerationDropdown"
  >
    <Sidebar v-if="showSidebar" />

    <div class="player-container">
      <DialogModal
        :show="showConfirmDialog"
        :title="$t('audioPlayer.generateSubtitlesTitle')"
        :close-label="$t('common.close')"
        variant="generation"
        @close="showConfirmDialog = false"
      >
        <template #header-leading>
          <span class="generation-dialog-mark" aria-hidden="true">
            <i></i><i></i><i></i><i></i>
          </span>
        </template>
        <template #subtitle>
          <p class="generation-dialog-intro">{{ $t('audioPlayer.generationSettingsHint') }}</p>
        </template>

        <div class="generation-settings-panel">
          <div class="generation-settings">
            <div class="generation-field">
              <span class="generation-field-label">{{ $t('audioPlayer.transcriptionModel') }}</span>
              <OptionSelect
                :model-value="selectedTranscriptionProvider"
                :options="transcriptionProviders"
                :open="openGenerationDropdown === 'provider'"
                :aria-label="$t('audioPlayer.transcriptionModel')"
                badge="AI"
                @toggle="toggleGenerationDropdown('provider')"
                @close="closeGenerationDropdown"
                @select="selectGenerationProvider"
              >
                <template #value="{ option }">
                  <span v-if="option">{{ $t('audioUploader.providers.' + option.labelKey) }}</span>
                </template>
                <template #option="{ option }">
                  {{ $t('audioUploader.providers.' + option.labelKey) }}
                </template>
              </OptionSelect>
            </div>
            <div class="generation-field">
              <span class="generation-field-label">{{ $t('audioPlayer.transcriptionLanguage') }}</span>
              <OptionSelect
                :model-value="selectedTranscriptionLanguage"
                :options="availableTranscriptionLanguages"
                :open="openGenerationDropdown === 'language'"
                :aria-label="$t('audioPlayer.transcriptionLanguage')"
                badge="A"
                @toggle="toggleGenerationDropdown('language')"
                @close="closeGenerationDropdown"
                @select="selectGenerationLanguage"
              >
                <template #value="{ option }">
                  <span v-if="option">{{ $t('audioUploader.languages.' + option.labelKey) }}</span>
                </template>
                <template #option="{ option }">
                  {{ $t('audioUploader.languages.' + option.labelKey) }}
                </template>
              </OptionSelect>
            </div>
          </div>
        </div>
        <div class="generation-usage">
          <span class="generation-usage-item">{{ $t('audioPlayer.generationUsage', { count: requiredUsage }) }}</span>
          <span class="generation-usage-item">{{ $t('materialCategory.currentPoints') }}: {{ remainingUsage }}</span>
        </div>
        <template #buttons>
          <button class="generation-cancel-btn" @click="showConfirmDialog = false">{{ $t('materialCategory.cancel') }}</button>
          <button class="generation-confirm-btn" @click="onConfirmGenerate">{{ $t('audioPlayer.generateSubtitlesAction') }}</button>
        </template>
      </DialogModal>

      <DialogModal
        :show="showNextLesson"
        :title="$t('audioPlayer.nextLesson.title')"
        :close-label="$t('common.close')"
        variant="next-lesson"
        @close="showNextLesson = false"
      >
        <p class="next-lesson-intro">{{ $t('audioPlayer.nextLesson.intro') }}</p>
        <div class="next-lesson-item">
          <span class="next-lesson-name">{{ nextLesson.title }}</span>
          <span class="next-lesson-meta">{{ nextLesson.level }} · {{ nextLesson.duration }}</span>
        </div>
        <template #buttons>
          <button class="generation-cancel-btn" type="button" @click="goToView('Material', {})">{{ $t('audioPlayer.nextLesson.library') }}</button>
          <button class="next-lesson-start" type="button" @click="startNextLesson">
            {{ user ? $t('audioPlayer.nextLesson.start') : $t('audioPlayer.nextLesson.signup') }}
          </button>
        </template>
      </DialogModal>

      <!-- 跟读评分时额度用完：先说明原因和恢复时间，再给升级入口；也可以关掉评分接着练 -->
      <DialogModal
        :show="showUsagePaywall"
        :title="$t('audioPlayer.usagePaywall.title')"
        :close-label="$t('common.close')"
        variant="usage-paywall"
        @close="showUsagePaywall = false"
      >
        <p class="next-lesson-intro">{{ $t('audioPlayer.usagePaywall.reason') }}</p>
        <ul class="usage-paywall-options">
          <li v-if="freeCreditsResetDate">{{ $t('audioPlayer.usagePaywall.reset', { date: freeCreditsResetDate, amount: maxMonthlyUsage }) }}</li>
          <li>{{ $t('audioPlayer.usagePaywall.checkIn', { amount: DAILY_REWARD }) }}</li>
          <li>{{ $t('audioPlayer.usagePaywall.noScore') }}</li>
        </ul>
        <template #buttons>
          <button class="generation-cancel-btn" type="button" @click="continueWithoutScore">{{ $t('audioPlayer.usagePaywall.continue') }}</button>
          <button class="generation-confirm-btn" type="button" @click="handleUpgrade()">{{ $t('audioPlayer.usagePaywall.upgrade') }}</button>
        </template>
      </DialogModal>

      <PlayerTopBar
        :audio-name="isDemo ? $t('audioPlayer.demoVideoName') : audioName"
        :task-type="taskType"
        :is-generating="isGenerating"
        :display-mode="displayMode"
        :is-read-along-mode="isReadAlongMode"
        :use-smart-split="useSmartSplit"
        :is-subtitles-visible="isSubtitlesVisible"
        :is-translated="isTranslated"
        :is-translating="isTranslating"
        :is-mobile="isMobile"
        :show-export-panel="isExportPanelVisible"
        :has-export-panel="!hasVisualMedia"
        :demo-translation-only="Boolean(videoSrc && isDemo)"
        :demo-video-mode="Boolean(videoSrc && isDemo)"
        :has-short-practice="shortPracticeSubtitles.length > 0"
        :has-pause="pauseSubtitles.length > 0"
        :split-type="splitType"
        :show-tour-button="canRunTour"
        @start-tour="startTour({ full: true })"
        @open-feedback="openPlayerFeedback"
        @back="goToHistory"
        @generate-subtitles="generateSubtitles(audioDuration)"
        @set-display-mode="displayMode = $event"
        @toggle-read-along="handleToggleReadAlongMode"
        @split-type-change="handleSplitTypeChange"
        @toggle-subtitles="toggleSubtitles"
        @translate-language="onTranslateLanguage"
        @toggle-export-panel="toggleExportPanel"
      />

      <PlayerMediaWorkspace
        :video-src="videoSrc"
        :youtube-video-id="youtubeVideoId"
        :poster-src="posterSrc"
        :is-playing="isPlaying"
        :subtitle-index="currentSubtitleIndex === null ? 0 : currentSubtitleIndex"
        :subtitle-count="displaySubtitles.length"
        @media-ready="attachVideoElement"
        @media-detached="detachVideoElement"
        @media-error="handleAudioError"
        @toggle-play="togglePlay"
      >
        <template #transcript>
          <SubtitleDisplay
            v-if="taskType !== 'tts' && displayMode === 'subtitles'"
            ref="subtitleDisplay"
            :subtitles="displaySubtitles"
            :translatedSubtitles="translatedSubtitles"
            :currentTime="currentTime"
            :playMode="playMode"
            :readAlongMode="readAlongMode"
            :audioId="audioId"
            :audio-name="audioName"
            :material-id="materialId"
            :material-book="materialBook"
            :displayMode="displayMode"
            :is-demo="isDemo"
            :editable="isOwnFile"
            :activeSubtitleIndex="currentSubtitleIndex"
            :show-timestamps="showTimestamps"
            :words="words"
            :isSubtitlesVisible="isSubtitlesVisible"
            :isWaitingForShadowing="isWaitingForShadowing"
            :shadowingStartTime="shadowingStartTime"
            :shadowingDuration="shadowingDuration"
            :is-playing="isPlaying"
            :fontSize="fontSize"
            :is-read-along-mode="isReadAlongMode"
            :recordings="recordings"
            :playing-recording-index="playingRecordingIndex"
            :assessments="assessments"
            :assistant-type="learningAssistant.open ? learningAssistant.type : ''"
            :assistant-index="learningAssistant.open ? learningAssistant.index : null"
            @update-subtitles="handleSubtitlesUpdate"
            @play-subtitle="playSubtitleAtIndex"
            @play-recording="playRecording"
            @open-learning-assistant="openLearningAssistant"
            class="subtitle-container"
          />

          <TextDisplay
            v-if="displayMode === 'text' || taskType === 'tts'"
            :text="originalText"
            :audioId="audioId"
            :filePath="filePath"
            :duration="audioDuration"
            class="subtitle-container"
          />
        </template>
      </PlayerMediaWorkspace>

      <PlayerReadAlongPanel
        :is-read-along-mode="isReadAlongMode"
        :task-type="taskType"
        :display-mode="displayMode"
        :read-along-mode="readAlongMode"
        :following-interval="followingInterval"
        :is-recording="isRecording"
        :microphone-level="microphoneLevel"
        :microphone-bars="microphoneBars"
        :waiting-for-recording="waitingForRecording"
        :is-playing="isPlaying"
        :is-paused="isPaused"
        :is-waiting-for-shadowing="isWaitingForShadowing"
        :vad-redemption-ms="vadRedemptionMs"
        :unfinished-pause-ms="unfinishedPauseMs"
        :microphone-devices="microphoneDevices"
        :selected-microphone-id="selectedMicrophoneId"
        :is-loading-microphones="isLoadingMicrophones"
        :recording-count="recordings.size"
        :total-recording-time="totalRecordingTime"
        :current-recording-time="currentRecordingTime"
        :is-playing-recordings="isPlayingRecordings"
        :pause-progress="pauseProgress"
        :is-holding-for-unfinished="isHoldingForUnfinished"
        :live-transcript="liveTranscript"
        :is-live-caption-lingering="isLiveCaptionLingering"
        :live-recognition-status="liveRecognitionStatus"
        :live-recognition-unavailable-reason="liveRecognitionUnavailableReason"
        :assessment-enabled="assessmentEnabled"
        :assessment-language="assessmentLanguage"
        @finish-speaking="finishSpeaking"
        @update:readAlongMode="readAlongMode = $event"
        @update:vadRedemptionMs="vadRedemptionMs = $event"
        @update:unfinishedPauseMs="unfinishedPauseMs = $event"
        @update:assessmentEnabled="assessmentEnabled = $event"
        @update:assessmentLanguage="assessmentLanguage = $event"
        @update:selectedMicrophoneId="setMicrophoneDevice"
        @refresh-microphones="refreshMicrophoneDevices()"
        @play-all-recordings="playCombinedRecordings"
        @download-recordings="downloadCombinedRecordings"
        @update:followingInterval="followingInterval = $event"
      />

      <PlayerAudioControls
        :icons="icons"
        :is-playing="isPlaying"
        :current-time="currentTime"
        :audio-duration="audioDuration"
        :progress-percentage="progressPercentage"
        :is-mobile="isMobile"
        :show-tooltip="showTooltip"
        :tooltip-position="tooltipPosition"
        :tooltip-time="tooltipTime"
        :show-font-size-control="showFontSizeControl"
        :min-font-size="minFontSize"
        :max-font-size="maxFontSize"
        :font-size="fontSize"
        :show-timestamps="showTimestamps"
        :show-volume-control="showVolumeControl"
        :volume="volume"
        :playback-rate="playbackRate"
        :show-speed-options="showSpeedOptions"
        :show-mode-options="showModeOptions"
        :play-mode="playMode"
        @previous-subtitle="playPreviousSubtitle"
        @toggle-play="togglePlay"
        @next-subtitle="playNextSubtitle"
        @progress-click="handleProgressClick"
        @progress-hover="handleProgressHover"
        @hide-time-tooltip="hideTimeTooltip"
        @toggle-font-size-control="toggleFontSizeControl"
        @hide-font-size-control="hideFontSizeControl"
        @set-font-size="setFontSize"
        @toggle-timestamps="toggleTimestamps"
        @toggle-volume-control="toggleVolumeControl"
        @hide-volume-control="hideVolumeControl"
        @set-volume="setVolume"
        @toggle-speed-options="toggleSpeedOptions"
        @hide-speed-options="hideSpeedOptions"
        @set-playback-rate="onSetPlaybackRate"
        @toggle-mode-options="toggleModeOptions"
        @hide-mode-options="hideModeOptions"
        @set-play-mode="setPlayMode"
      />
    </div>

    <Transition name="assistant-panel">
    <LearningAssistantPanel
      v-if="learningAssistant.open"
      :type="learningAssistant.type"
      :subtitle="learningAssistant.subtitle"
      :assessment="learningAssistant.assessment"
      :explanation="learningAssistant.explanation"
      :explanation-quota="learningAssistant.explanationQuota"
      @close="closeLearningAssistant"
      @select-tab="selectLearningAssistantTab"
      @generate-explanation="generateAssistantExplanation"
      @ask-follow-up="askAssistantFollowUp"
      @toggle-save-explanation="toggleAssistantExplanationSaved"
    />
    </Transition>

    <ExportPanel
      v-if="!learningAssistant.open && showExportPanel"
      :show="isExportPanelVisible"
      :audio-id="audioId"
      :audio-name="audioName"
      :subtitles="subtitles"
      :allow-audio-download="allowAudioDownload"
      :own-file="isOwnFile"
      @download-audio="onDownloadAudio"
      @delete-file="onDeleteFile"
      @export="handleExport"
    />

    <PlayerTour
      v-if="activeTourStep"
      :step="activeTourStep"
      :index="tourStepIndex - tourFirstStepIndex"
      :total="tourSteps.length - tourFirstStepIndex"
      :skip-label="$t('audioPlayer.tour.skip')"
      @primary="handleTourPrimary"
      @secondary="handleTourSecondary"
      @skip="endTour"
    />
  </div>
</template>

<script>
import { ref, computed, toRef, watch } from 'vue'
import { ElMessage } from 'element-plus'
import SubtitleDisplay from './SubtitleDisplay.vue'
import TextDisplay from './TextDisplay.vue'
import ExportPanel from './ExportPanel.vue'
import LearningAssistantPanel from './LearningAssistantPanel.vue'
import PlayerTopBar from './PlayerTopBar.vue'
import PlayerReadAlongPanel from './PlayerReadAlongPanel.vue'
import PlayerAudioControls from './PlayerAudioControls.vue'
import PlayerMediaWorkspace from './PlayerMediaWorkspace.vue'
import PlayerTour from './PlayerTour.vue'
import DialogModal from '../modals/DialogModal.vue'
import OptionSelect from '../common/OptionSelect.vue'
import Sidebar from '../layout/Sidebar.vue'
import { auth } from '@/firebase'
import { flushListeningActivity } from '@/utils/listeningActivity'
import { rememberStudySource } from '@/utils/lastStudySource'
import pastOneIcon from '@/assets/ui-icons/previous.svg'
import nextOneIcon from '@/assets/ui-icons/next.svg'
import playIcon from '@/assets/ui-icons/play.svg'
import pauseIcon from '@/assets/ui-icons/pause.svg'
import repeatIcon from '@/assets/ui-icons/repeat.svg'
import volumeIcon from '@/assets/ui-icons/volume.svg'
import speedIcon from '@/assets/ui-icons/clock.svg'
import fontSizeIcon from '@/assets/ui-icons/font.svg'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import { useViews } from '@/composables/views'
import { useLearnerProfile } from '@/composables/useLearnerProfile'
import { nextLessonAfterDemo } from '@/composables/demoLesson'
import { DAILY_REWARD } from '@/utils/studyProgress'
import { useSubtitleTranslation } from '@/composables/player/useSubtitleTranslation'
import { useSubtitleGeneration } from '@/composables/player/useSubtitleGeneration'
import { useReadAlong } from '@/composables/player/useReadAlong'
import { isAssessmentPreferred, isReadAlongPreferred, saveAssessmentPreference } from '@/composables/player/readAlongPreference'
import { openFeedback } from '@/composables/useFeedback'
import { assessmentReferenceText, usePronunciationAssessment } from '@/composables/player/usePronunciationAssessment'
import {
  DEFAULT_PRONUNCIATION_LANGUAGE,
  PRONUNCIATION_LANGUAGE_CODES,
} from '@/composables/player/pronunciationLanguages'
import { useAudioPlayback } from '@/composables/player/useAudioPlayback'
import { useVideoPlayback } from '@/composables/player/useVideoPlayback'
import { usePlaybackControl } from '@/composables/player/usePlaybackControl'
import { useFileOperations } from '@/composables/player/useFileOperations'

const COMPACT_PLAYER_BREAKPOINT = 1100
const DEFAULT_SUBTITLE_FONT_SIZE = 20
const MIN_SUBTITLE_FONT_SIZE = 12
const MAX_SUBTITLE_FONT_SIZE = 32

const getSubtitleFontSizeStorageKey = () =>
  `goodscriber:subtitle-font-size:${auth.currentUser?.uid || 'guest'}`

const clampSubtitleFontSize = (value) => {
  const parsedValue = Number.parseInt(value, 10)
  if (!Number.isFinite(parsedValue)) return DEFAULT_SUBTITLE_FONT_SIZE
  return Math.min(MAX_SUBTITLE_FONT_SIZE, Math.max(MIN_SUBTITLE_FONT_SIZE, parsedValue))
}

const getStoredSubtitleFontSize = () =>
  clampSubtitleFontSize(window.localStorage.getItem(getSubtitleFontSizeStorageKey()))

export default {
  name: 'AudioPlayer',
  components: {
    SubtitleDisplay,
    TextDisplay,
    ExportPanel,
    LearningAssistantPanel,
    PlayerTopBar,
    PlayerReadAlongPanel,
    PlayerAudioControls,
    PlayerMediaWorkspace,
    PlayerTour,
    DialogModal,
    OptionSelect,
    Sidebar,
  },
  props: {
    audioSrc: { type: String, default: '' },
    videoSrc: { type: String, default: '' },
    youtubeVideoId: { type: String, default: '' },
    posterSrc: { type: String, default: '' },
    // 素材封面 { src, artwork }，只用于“继续上次练习”的卡片；artwork 表示播客那种方形节目图
    cover: { type: Object, default: null },
    audioId: { type: String, required: true },
    materialId: { type: String, default: '' },
    materialBook: { type: String, default: '' },
    // 播客素材直接播放原地址，不提供下载
    allowAudioDownload: { type: Boolean, default: true },
    audioName: { type: String, required: true },
    taskType: { type: String, required: true },
    originalSubtitles: { type: Array, default: () => [] },
    subtitles: { type: Array, default: () => [] },
    practiceSubtitles: { type: Array, default: () => [] },
    shortPracticeSubtitles: { type: Array, default: () => [] },
    pauseSubtitles: { type: Array, default: () => [] },
    completeSubtitles: { type: Array, default: () => [] },
    words: { type: Array, default: () => [] },
    demoTranslations: { type: Object, default: null },
    originalText: { type: String, default: '' },
    filePath: { type: String, default: '' },
    isDemo: { type: Boolean, default: false },
    useSmartSplit: { type: Boolean, default: false },
    returnView: { type: String, default: '' },
    returnParams: { type: Object, default: () => ({}) },
    focusSentence: { type: Object, default: null },
    // 从“使用教程”入口进来时带一个新值，用来重新开始引导
    tourRequest: { type: Number, default: 0 },
  },
  emits: ['subtitles-updated', 'success', 'error', 'audio-error', 'file-updated', 'update:taskType', 'refresh-files'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const { handleUpgrade, showSignupModal } = useModals()
    const { user, userData, userDetails } = useAuth()
    const showUsagePaywall = ref(false)
    let usagePaywallShown = false
    // 免费额度按上次重置日每月恢复一次（同一天），付费用户跟着续费走
    const freeCreditsResetDate = computed(() => {
      const details = userDetails.value
      if (details.isPro || details.isPremium || details.canManageSubscription) return ''
      const raw = userData.value?.quota?.lastResetAt
      if (!raw) return ''
      const last = typeof raw.toDate === 'function' ? raw.toDate() : new Date(raw)
      const next = new Date(last.getFullYear(), last.getMonth() + 1, last.getDate())
      return next.toLocaleDateString(t('audioPlayer.usagePaywall.dateLocale'), { month: 'long', day: 'numeric' })
    })
    const { profile } = useLearnerProfile()
    const { currentView, handleViewChange: viewChange } = useViews()

    const isPremium = computed(() => userDetails.value?.isPremium || false)
    const isPro = computed(() => userDetails.value?.isPro || false)
    const remainingUsage = computed(() => userDetails.value?.remainingUsage || 0)
    const maxMonthlyUsage = computed(() => userDetails.value?.maxMonthlyUsage || 200)
    const userPhotoURL = computed(() => user.value?.photoURL || '')

    const handleViewChange = (view, params) => {
      viewChange(view, params)
    }

    const translation = useSubtitleTranslation()
    const generation = useSubtitleGeneration()
    const pronunciationAssessment = usePronunciationAssessment()
    const storedAssessmentLanguage = window.localStorage.getItem('pronunciationAssessmentLanguage')
    const assessmentEnabled = ref(isAssessmentPreferred())
    const assessmentLanguage = ref(
      PRONUNCIATION_LANGUAGE_CODES.has(storedAssessmentLanguage)
        ? storedAssessmentLanguage
        : DEFAULT_PRONUNCIATION_LANGUAGE
    )

    watch(assessmentEnabled, (value) => {
      void saveAssessmentPreference(value)
    })
    watch(assessmentLanguage, (value) => {
      if (PRONUNCIATION_LANGUAGE_CODES.has(value)) {
        window.localStorage.setItem('pronunciationAssessmentLanguage', value)
      }
    })

    const playMode = ref('auto')
    const splitType = ref('practice')

    const displaySubtitles = computed(() => {
      if (splitType.value === 'practice') {
        return props.practiceSubtitles?.length
          ? props.practiceSubtitles
          : (props.originalSubtitles.length ? props.originalSubtitles : props.subtitles)
      }
      if (splitType.value === 'practice_short') return props.shortPracticeSubtitles
      if (splitType.value === 'pause') return props.pauseSubtitles
      if (splitType.value === 'comma') return props.subtitles
      if (splitType.value === 'period') return props.completeSubtitles
      if (props.originalSubtitles.length === 0) return props.subtitles
      return props.originalSubtitles
    })

    const audioVisualizer = ref(null)
    const setAudioVisualizerRef = (element) => {
      audioVisualizer.value = element || null
    }

    const playbackOptions = {
      displaySubtitles,
      playMode,
      onErrorCallback: (error) => emit('audio-error', error),
    }
    const playback = (props.videoSrc || props.youtubeVideoId)
      ? useVideoPlayback(playbackOptions)
      : useAudioPlayback({
          ...playbackOptions,
          audioSrc: toRef(props, 'audioSrc'),
        })

    // 复述跟读只录音，不评分
    const isScoring = () => assessmentEnabled.value && readAlong.readAlongMode.value === 'recording'

    const readAlong = useReadAlong({
      sound: playback.sound,
      isPlaying: playback.isPlaying,
      currentSubtitleIndex: playback.currentSubtitleIndex,
      displaySubtitles,
      pauseCurrent: playback.pauseCurrent,
      seekCurrent: playback.seekCurrent,
      audioVisualizerRef: audioVisualizer,
      showError: (key) => ElMessage.error(t(key)),
      getMaxRecordingMs: () => (isScoring() ? 30000 : 60000),
      getRecognitionLanguage: () => assessmentLanguage.value,
      // 实时识别和评分是同一次识别，跟着评分开关走
      liveRecognitionEnabled: assessmentEnabled,
      // 素材的 audioId 是分类级（materials-<分类>），需拼上 materialId 才能区分同分类下的不同素材。
      // 录音按句子序号保存，换一种分句后序号对应的句子就变了，所以每种分句各存一份
      recordingSessionKey: computed(() => (
        `${props.materialId ? `${props.audioId}:${props.materialId}` : props.audioId}:${splitType.value}`
      )),
      onRecordingReady: async ({ index, blob, duration, subtitle, liveAssessment }) => {
        if (!isScoring()) {
          pronunciationAssessment.clearAssessment(index)
          return
        }
        const assessmentResult = await pronunciationAssessment.assessSubtitleRecording({
          index,
          blob,
          duration,
          liveAssessment,
          referenceText: subtitle?.text || '',
          language: assessmentLanguage.value,
          audioId: props.audioId,
          audioName: props.audioName,
          materialId: props.materialId,
          materialBook: props.materialBook,
          isDemo: props.isDemo,
        })
        // 每次打开播放器只弹一次，关掉后后面的句子只在行内标出评分失败
        if (!assessmentResult?.success && assessmentResult?.code === 'INSUFFICIENT_USAGE' && !usagePaywallShown) {
          usagePaywallShown = true
          showUsagePaywall.value = true
        }
        if (!assessmentResult?.success && assessmentResult?.code === 'ANONYMOUS_DAILY_LIMIT') {
          ElMessage.error(t('audioPlayer.pronunciationAssessment.anonymousLimit'))
        }
      },
    })

    watch(
      () => [props.audioId, props.audioName, props.materialId, props.materialBook, props.isDemo],
      () => {
        pronunciationAssessment.loadAssessmentHistory({
          audioId: props.audioId,
          audioName: props.audioName,
          materialId: props.materialId,
          materialBook: props.materialBook,
          isDemo: props.isDemo,
        })
      },
      { immediate: true }
    )

    const control = usePlaybackControl({
      sound: playback.sound,
      isPlaying: playback.isPlaying,
      currentSubtitleIndex: playback.currentSubtitleIndex,
      currentTime: playback.currentTime,
      displaySubtitles,
      playCurrent: playback.playCurrent,
      pauseCurrent: playback.pauseCurrent,
      stopCurrent: playback.stopCurrent,
      seekCurrent: playback.seekCurrent,
      isCurrentPlaying: playback.isCurrentPlaying,
      isReadAlongMode: readAlong.isReadAlongMode,
      readAlongMode: readAlong.readAlongMode,
      isRecording: readAlong.isRecording,
      isPaused: readAlong.isPaused,
      isWaitingForShadowing: readAlong.isWaitingForShadowing,
      isPausedWhileShadowing: readAlong.isPausedWhileShadowing,
      waitingForRecording: readAlong.waitingForRecording,
      stopRecording: readAlong.stopRecording,
      clearAllTimers: readAlong.clearAllTimers,
      setupPauseTimer: readAlong.setupPauseTimer,
      setupFollowingTimer: readAlong.setupFollowingTimer,
      closeMediaStream: readAlong.closeMediaStream,
      setOnPauseCallback: playback.setOnPauseCallback,
      setOnEndedCallback: playback.setOnEndedCallback,
      setPlayNextSubtitle: readAlong.setPlayNextSubtitle,
      onPause: readAlong.onPause,
      handleViewChange,
    })

    const fileOps = useFileOperations()

    // 评分也按句子序号保存，只在评分时的原文和这一行现在的文字一致时才显示
    const assessments = computed(() => new Map(
      Array.from(pronunciationAssessment.assessments.value).filter(([index, assessment]) => (
        assessment.referenceText === assessmentReferenceText(displaySubtitles.value[index]?.text)
      ))
    ))

    return {
      handleUpgrade,
      showUsagePaywall,
      freeCreditsResetDate,
      DAILY_REWARD,
      showSignupModal,
      user,
      profile,
      isPremium,
      isPro,
      remainingUsage,
      maxMonthlyUsage,
      userPhotoURL,
      handleViewChange,
      currentView,
      ...translation,
      ...generation,
      ...pronunciationAssessment,
      ...playback,
      attachVideoElement: playback.attachVideoElement || (() => {}),
      detachVideoElement: playback.detachVideoElement || (() => {}),
      ...control,
      ...fileOps,
      playMode,
      splitType,
      displaySubtitles,
      audioVisualizer,
      setAudioVisualizerRef,
      assessmentEnabled,
      assessmentLanguage,
      ...readAlong,
      assessments,
    }
  },
  data() {
    return {
      instanceId: Date.now(),
      useSmartSplit: true,
      icons: {
        pastOne: pastOneIcon,
        nextOne: nextOneIcon,
        play: playIcon,
        pause: pauseIcon,
        repeat: repeatIcon,
        volume: volumeIcon,
        speed: speedIcon,
        fontSize: fontSizeIcon,
      },
      showTimestamps: false,
      showSidebar: !(this.videoSrc || this.youtubeVideoId),
      showExportPanel: !(this.videoSrc || this.youtubeVideoId) && window.innerWidth > COMPACT_PLAYER_BREAKPOINT,
      pendingFocus: null,
      hasPlayedSource: false,
      learningAssistant: {
        open: false,
        type: 'explanation',
        index: null,
        subtitle: null,
        assessment: null,
        explanation: null,
        explanationQuota: null,
      },
      showVolumeControl: false,
      showSpeedOptions: false,
      showModeOptions: false,
      isMobile: window.innerWidth <= 768,
      isCompactLayout: window.innerWidth <= COMPACT_PLAYER_BREAKPOINT,
      displayMode: 'subtitles',
      isSubtitlesVisible: true,
      showFontSizeControl: false,
      fontSize: getStoredSubtitleFontSize(),
      minFontSize: MIN_SUBTITLE_FONT_SIZE,
      maxFontSize: MAX_SUBTITLE_FONT_SIZE,
      tourStepIndex: null,
      tourFirstStepIndex: 0,
      // 自动开始的示范引导只教跟读本身，从“使用教程”进来才逐个介绍设置
      tourFull: false,
      // 示范素材练完后推荐下一条，每次打开示范只弹一次
      showNextLesson: false,
      nextLessonOffered: false,
      // 进入“开口跟读”这一步时各句的评分，之后新出的分数才算这次引导里读的
      tourAssessmentBaseline: null,
      tourRecordingBaseline: null,
      tourScoredIndex: null,
      // 引导按智能跟读来教，开始前用的其他跟读方式在结束时换回去
      tourPreviousReadAlongMode: null,
    }
  },
  computed: {
    // 素材库内容和试用示例不是用户自己的文件，不能改字幕、不能删除
    isOwnFile() {
      return !this.materialId && !this.isDemo
    },
    isExportPanelVisible() {
      return this.showExportPanel && !this.learningAssistant.open
    },
    hasVisualMedia() {
      return Boolean(this.videoSrc || this.youtubeVideoId)
    },
    // 字幕跟读界面都能运行操作引导；示范素材打开时自动开始
    canRunTour() {
      return this.taskType !== 'tts' && this.displayMode === 'subtitles'
    },
    tourNewScoredIndex() {
      if (!this.tourAssessmentBaseline) return null
      const entries = Array.from(this.assessments.entries())
        .filter(([index, assessment]) => assessment?.status === 'done' && this.tourAssessmentBaseline.get(index) !== assessment)
      return entries.length ? entries[entries.length - 1][0] : null
    },
    // 关掉评分时，这次引导里新录下一句就算读完
    tourHasNewRecording() {
      if (!this.tourRecordingBaseline) return false
      return Array.from(this.recordings.entries())
        .some(([index, recording]) => this.tourRecordingBaseline.get(index) !== recording)
    },
    tourAssessingIndex() {
      if (!this.canRunTour || !(this.assessments instanceof Map)) return null

      const entries = Array.from(this.assessments.entries())
        .filter(([index, assessment]) => assessment?.status === 'assessing' && this.displaySubtitles?.[index])

      if (!entries.length) return null
      return entries[entries.length - 1][0]
    },
    // 示范素材的操作引导：前三步要用户亲手做一遍跟读，之后逐个介绍设置
    tourSteps() {
      const t = (key) => this.$t(`audioPlayer.tour.${key}`)
      if (!this.tourFull) {
        return this.fullTourSteps
          .filter((step) => ['enable', 'listen', 'speak', 'score'].includes(step.key))
          .concat({
            key: 'finish',
            title: t('finishShort.title'),
            body: t('finishShort.body'),
            primaryLabel: t('finishShort.action'),
            secondaryLabel: t('finishShort.more'),
            // 接着走完整引导里介绍设置的几步，编号从这里重新算
            secondaryRun: () => {
              this.tourFull = true
              this.tourFirstStepIndex = this.fullTourSteps.findIndex((step) => step.key === 'modes')
              this.goToTourStep(this.tourFirstStepIndex)
            },
          })
      }
      return this.fullTourSteps
    },
    fullTourSteps() {
      const t = (key) => this.$t(`audioPlayer.tour.${key}`)
      return [
        {
          key: 'enable',
          target: '.action-readalong',
          waitsForAction: true,
          primaryLabel: t('enable.action'),
          run: () => this.handleToggleReadAlongMode(),
          done: () => this.isReadAlongMode,
        },
        {
          key: 'listen',
          target: '.media-workspace, .audio-controls',
          waitsForAction: true,
          primaryLabel: t('listen.action'),
          run: () => this.togglePlay(),
          done: () => this.waitingForRecording || this.isRecording,
          // 重看教程时练习可能正停在等开口，这一步要用户重新播一句
          skipWhenDone: false,
        },
        {
          key: 'speak',
          target: '.read-along-main',
          waitsForAction: true,
          // 评分要等几秒，出分前练习照常往下走
          body: !this.assessmentEnabled
            ? t('speak.bodyNoScore')
            : this.tourAssessingIndex !== null ? t('speak.scoring') : t('speak.body'),
          done: () => (this.assessmentEnabled ? this.tourNewScoredIndex !== null : this.tourHasNewRecording),
          skipWhenDone: false,
        },
        // 介绍类步骤的高亮处都能直接点，展开的面板一并算进高亮区
        {
          key: 'score',
          target: `[data-subtitle-index="${this.tourScoredIndex}"] .assessment-badge, .learning-assistant`,
          primaryLabel: t('next'),
          // 评分关着就没有分数可看，跳过这一步
          done: () => !this.assessmentEnabled,
        },
        { key: 'modes', target: '.mode-menu-trigger, .mode-menu-popup', primaryLabel: t('next') },
        { key: 'settings', target: '.mic-settings .recording-tool-button, .vad-settings, .player-select-popper', primaryLabel: t('next') },
        { key: 'split', target: '.action-split, .el-dropdown__popper', primaryLabel: t('next') },
        { key: 'translate', target: '.action-translate, .el-dropdown__popper', primaryLabel: t('next') },
        { key: 'playback', target: '.playback-settings, .setting-popover', primaryLabel: t('next') },
        {
          key: 'finish',
          primaryLabel: t('finish.library'),
          secondaryLabel: t('finish.stay'),
          run: () => this.goToView('Material', {}),
        },
      ].map((step) => ({ title: t(`${step.key}.title`), body: t(`${step.key}.body`), ...step }))
    },
    activeTourStep() {
      return this.tourStepIndex === null ? null : this.tourSteps[this.tourStepIndex]
    },
    isTourActionDone() {
      return Boolean(this.activeTourStep?.done?.())
    },
    nextLesson() {
      return nextLessonAfterDemo(this.profile.listeningLevel)
    },
    // 最后一句播完、录完、评完分，示范就算练完了
    demoFinished() {
      const subtitles = this.displaySubtitles
      if (!this.isDemo || !subtitles?.length || this.activeTourStep) return false
      if (this.currentSubtitleIndex !== subtitles.length - 1) return false
      if (this.isPlaying || this.isRecording || this.waitingForRecording) return false
      if (Array.from(this.assessments.values()).some((assessment) => assessment?.status === 'assessing')) return false
      return this.currentTime >= subtitles[subtitles.length - 1].end - 0.3
    },
  },
  watch: {
    focusSentence: {
      immediate: true,
      handler(focus) {
        this.pendingFocus = focus
        this.$nextTick(() => this.applyPendingFocus())
      },
    },
    audioDuration() {
      this.applyPendingFocus()
    },
    displaySubtitles() {
      this.applyPendingFocus()
    },
    isDemo(newValue, oldValue) {
      if (oldValue && !newValue) {
        this.resetDemoReadAlongState()
        this.endTour()
      }
      if (newValue && !oldValue) this.nextLessonOffered = false
      if (newValue && !oldValue && this.canRunTour) {
        this.startTour()
      }
    },
    tourRequest(request) {
      if (request) this.startTour({ full: true })
    },
    // 记下最近听的内容和听到哪一句，打卡页和素材库从这里续上；只打开没播放的不算
    isPlaying(playing) {
      if (!playing || this.isDemo) return
      this.hasPlayedSource = true
      this.rememberPlaybackPosition()
    },
    currentSubtitleIndex() {
      if (this.hasPlayedSource) this.rememberPlaybackPosition()
    },
    // 弹出额度说明时先停下练习，免得一边看说明一边自动进入下一句
    showUsagePaywall(open) {
      if (!open) return
      this.pausePracticeForTour()
      this.pauseCurrent?.({ suppressCallback: true })
    },
    demoFinished(finished) {
      if (!finished || this.nextLessonOffered) return
      this.nextLessonOffered = true
      this.showNextLesson = true
    },
    isTourActionDone(done) {
      if (done) this.goToTourStep(this.tourStepIndex + 1)
    },
    audioId(newValue, oldValue) {
      this.hasPlayedSource = false
      if (newValue !== oldValue && !this.isDemo) {
        this.resetDemoReadAlongState()
        this.endTour()
      }
    },
    currentView(newView) {
      if (newView !== 'AudioPlayer') {
        this.endTour()
        if (this.sound) {
          this.sound.stop()
          this.isPlaying = false
        }
        if (this.isRecording) {
          this.stopRecording()
        }
        this.closeMediaStream?.()
        this.clearAllTimers?.()
      }
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    window.currentAudioPlayer = this.instanceId
    window.addEventListener('keydown', this.handleKeydown)
    this.injectCallbacks()
    this.setOnPlayCallback(this.onPlaybackStarted)
  },
  mounted() {
    if (this.isDemo && this.canRunTour) this.startTour({ full: Boolean(this.tourRequest) })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.handleKeydown)

    if (window.currentAudioPlayer === this.instanceId) {
      window.currentAudioPlayer = null
    }

    this.cleanupPlayback()
    this.cleanupReadAlong()
    flushListeningActivity()
  },
  deactivated() {
    this.endTour()
    this.cleanupPlayback()
    this.cleanupReadAlong()
  },
  methods: {
    // 每次打开示范素材都从头走一遍引导，可随时跳过
    startTour({ full = false } = {}) {
      this.tourFull = full
      if (this.isPlaying) this.pauseCurrent?.({ suppressCallback: true })
      this.pausePracticeForTour()
      this.tourAssessmentBaseline = null
      this.tourRecordingBaseline = null
      this.tourScoredIndex = null
      if (this.readAlongMode !== 'recording') {
        this.tourPreviousReadAlongMode = this.readAlongMode
        this.readAlongMode = 'recording'
      }
      this.goToTourStep(0)
      // 开头已经做完的步骤不计入进度，编号从用户看到的第一步算起
      this.tourFirstStepIndex = this.tourStepIndex ?? 0
    },
    endTour() {
      this.tourStepIndex = null
      // 用户在引导里自己换了跟读方式就不再改回
      if (this.tourPreviousReadAlongMode && this.readAlongMode === 'recording') {
        this.readAlongMode = this.tourPreviousReadAlongMode
      }
      this.tourPreviousReadAlongMode = null
    },
    // 已经做完的操作步骤直接跳过，例如跟读已经开着
    goToTourStep(index) {
      let next = index
      while (this.tourSteps[next]?.skipWhenDone !== false && this.tourSteps[next]?.done?.()) next += 1
      if (next >= this.tourSteps.length) {
        this.endTour()
        return
      }
      // 进入介绍环节前先停下练习，免得一边讲解一边自动播放下一句
      if (!this.tourSteps[next].waitsForAction && this.tourSteps[this.tourStepIndex ?? 0].waitsForAction) {
        this.pausePracticeForTour()
        this.pauseCurrent?.({ suppressCallback: true })
      }
      if (this.tourSteps[this.tourStepIndex]?.key === 'speak') this.tourScoredIndex = this.tourNewScoredIndex
      if (this.tourSteps[next].key === 'speak') {
        this.tourAssessmentBaseline = new Map(this.assessments)
        this.tourRecordingBaseline = new Map(this.recordings)
      }
      this.tourStepIndex = next
    },
    continueWithoutScore() {
      this.showUsagePaywall = false
      this.assessmentEnabled = false
    },
    startNextLesson() {
      this.showNextLesson = false
      if (!this.user) {
        this.showSignupModal()
        return
      }
      const { categoryId, collectionId, itemId } = this.nextLesson
      this.goToView('Material', { categoryId, collectionId, play: itemId })
    },
    handleTourSecondary() {
      const step = this.activeTourStep
      if (step.secondaryRun) step.secondaryRun()
      else this.endTour()
    },
    handleTourPrimary() {
      const step = this.activeTourStep
      if (step.key === 'finish') {
        this.endTour()
        step.run?.()
        return
      }
      if (step.key === 'score') this.closeLearningAssistant()
      if (step.run) step.run()
      else this.goToTourStep(this.tourStepIndex + 1)
    },
    playCombinedRecordings() {
      if (!this.isPlayingRecordings && this.isPlaying) {
        this.pauseCurrent?.({ suppressCallback: true })
        this.isPlaying = false
      }
      this.playAllRecordings()
    },
    downloadCombinedRecordings() {
      this.downloadRecordings(this.audioName, this.$t('audioPlayer.recordingFileSuffix'))
    },
    openLearningAssistant(payload) {
      this.learningAssistant = {
        open: true,
        type: payload.type,
        index: payload.index,
        subtitle: payload.subtitle,
        assessment: payload.assessment,
        explanation: payload.explanation,
        explanationQuota: payload.explanationQuota,
      }
    },
    // 从播放器提交反馈时带上素材和当前句，排查时能直接定位
    openPlayerFeedback() {
      const index = this.currentSubtitleIndex
      openFeedback({
        audioName: this.isDemo ? this.$t('audioPlayer.demoVideoName') : this.audioName,
        audioId: this.audioId,
        materialId: this.materialId,
        sentenceIndex: index === null ? undefined : index + 1,
        sentenceText: index === null ? undefined : this.displaySubtitles[index]?.text,
        currentTime: Math.round(this.currentTime * 10) / 10,
      })
    },
    closeLearningAssistant() {
      this.learningAssistant.open = false
    },
    selectLearningAssistantTab(type) {
      const index = this.learningAssistant.index
      if (!Number.isInteger(index)) return
      if (type === 'assessment') {
        this.$refs.subtitleDisplay?.openAssessmentAssistant(index)
        return
      }
      this.$refs.subtitleDisplay?.openSentenceExplanation(index)
    },
    generateAssistantExplanation() {
      const index = this.learningAssistant.index
      if (!Number.isInteger(index)) return
      this.$refs.subtitleDisplay?.generateSentenceExplanation(index)
    },
    askAssistantFollowUp(kind) {
      const index = this.learningAssistant.index
      if (!Number.isInteger(index)) return
      this.$refs.subtitleDisplay?.askExplanationFollowUp(index, kind)
    },
    rememberPlaybackPosition() {
      const index = this.currentSubtitleIndex
      rememberStudySource(auth.currentUser?.uid, {
        audioId: this.audioId,
        audioName: this.audioName,
        materialId: this.materialId,
        materialBook: this.materialBook,
        subtitleIndex: Number.isInteger(index) ? index : null,
        sentence: Number.isInteger(index) ? this.displaySubtitles[index]?.text || '' : '',
        subtitleCount: this.displaySubtitles.length,
        cover: this.cover || (this.posterSrc ? { src: this.posterSrc, artwork: false } : null),
      })
    },
    toggleAssistantExplanationSaved() {
      const index = this.learningAssistant.index
      if (!Number.isInteger(index)) return
      this.$refs.subtitleDisplay?.toggleExplanationSaved(index)
    },
    goToHistory() {
      const fallbackView = this.isDemo ? 'HomePage' : 'RecentFiles'
      this.goToView(this.returnView || fallbackView, this.returnParams || {})
    },
    handleSubtitlesUpdate(updatedSubtitles) {
      this.$emit('subtitles-updated', updatedSubtitles)
    },
    async onConfirmGenerate() {
      const result = await this.confirmGenerate({
        audioId: this.audioId,
        originalText: this.originalText,
        remainingUsage: this.remainingUsage,
        transcriptionProvider: this.selectedTranscriptionProvider,
        transcriptionLanguage: this.selectedTranscriptionLanguage,
      })

      if (result.insufficientUsage) {
        this.handleUpgrade()
        return
      }

      if (result.success) {
        this.$emit('update:taskType', 'tts-stt')
        this.displayMode = 'subtitles'
        if (result.fileData) {
          this.$emit('subtitles-updated', result.fileData.subtitles)
          this.$emit('file-updated', result.fileData)
          this.$emit('refresh-files')
        }
        this.$emit('success', this.$t('common.subtitlesGenerated'))
      } else if (result.error) {
        this.$emit('error', String(result.error || this.$t('common.generateSubtitlesFailed')))
      }
    },
    async onDeleteFile() {
      try {
        await this.handleDeleteFile(this.audioId)
        this.$emit('success', { message: 'File deleted successfully' })
        this.goToHistory()
      } catch (error) {
        this.$emit('error', String(error.message || 'Failed to delete file'))
      }
    },
    async onDownloadAudio() {
      try {
        await this.handleDownloadAudio(this.audioSrc, this.audioName)
      } catch (error) {
        this.$emit('error', String(error.message || 'Failed to download audio'))
      }
    },
    // 引导进入讲解环节时停下练习
    pausePracticeForTour() {
      this.clearAllTimers?.()
      this.isPaused = true
      this.isPlaying = false
      this.isWaitingForShadowing = false
      this.isPausedWhileShadowing = false

      if (this.isRecording || this.waitingForRecording) {
        this.stopRecording?.()
      }

      this.waitingForRecording = false
      this.isRecording = false
      this.stopPlayingRecordings?.()
    },
    applyPendingFocus() {
      const focus = this.pendingFocus
      if (!focus || !this.displaySubtitles?.length) return
      const normalize = (text) => String(text || '').replace(/\s+/g, ' ').trim().toLowerCase()
      const matchedIndex = this.displaySubtitles.findIndex((subtitle) => normalize(subtitle.text) === normalize(focus.text))
      const index = matchedIndex >= 0 ? matchedIndex : focus.index
      const subtitle = this.displaySubtitles[index]
      if (!subtitle) {
        this.pendingFocus = null
        return
      }
      this.currentSubtitleIndex = index
      this.$nextTick(() => this.$refs.subtitleDisplay?.scrollToSubtitle(index))
      if (!(this.audioDuration > 0)) return
      const startTime = Number(subtitle.start) || 0
      this.currentTime = startTime
      this.progressPercentage = (startTime / this.audioDuration) * 100
      this.seekCurrent?.(startTime)
      this.pendingFocus = null
    },
    resetDemoReadAlongState() {
      if (this.sound) {
        this.sound.stop()
        this.isPlaying = false
      }
      if (this.isRecording) {
        this.stopRecording()
      }
      this.isReadAlongMode = isReadAlongPreferred()
      this.readAlongMode = 'recording'
      this.cleanupReadAlong?.()
    },
    toggleExportPanel() {
      if (this.learningAssistant.open) {
        this.learningAssistant.open = false
        this.showExportPanel = true
        return
      }
      this.showExportPanel = !this.showExportPanel
    },
    toggleTimestamps() {
      this.showTimestamps = !this.showTimestamps
    },
    closePlaybackSettings() {
      this.showFontSizeControl = false
      this.showVolumeControl = false
      this.showSpeedOptions = false
      this.showModeOptions = false
    },
    async handleToggleReadAlongMode() {
      this.closePlaybackSettings()
      const shouldPauseBeforeOpeningReadAlong = !this.isReadAlongMode

      if (shouldPauseBeforeOpeningReadAlong) {
        this.pauseBeforeOpeningReadAlong()
      }

      await this.toggleReadAlongMode()
    },
    pauseBeforeOpeningReadAlong() {
      this.clearAllTimers?.()

      if (this.isRecording || this.waitingForRecording) {
        this.stopRecording?.()
      }

      this.waitingForRecording = false
      this.isRecording = false
      this.isWaitingForShadowing = false
      this.isPausedWhileShadowing = false
      this.isPaused = true

      if (this.sound && this.isPlaying) {
        this.pauseCurrent?.()
      }

      this.isPlaying = false
    },
    handleResize() {
      const viewportWidth = window.innerWidth
      const wasCompact = this.isCompactLayout

      this.isMobile = viewportWidth <= 768
      this.isCompactLayout = viewportWidth <= COMPACT_PLAYER_BREAKPOINT

      if (this.hasVisualMedia || this.isCompactLayout) {
        this.showExportPanel = false
      } else if (wasCompact) {
        this.showExportPanel = true
      }
    },
    toggleVolumeControl() {
      this.showVolumeControl = !this.showVolumeControl
      if (this.showVolumeControl) {
        this.showFontSizeControl = false
        this.showSpeedOptions = false
        this.showModeOptions = false
      }
    },
    hideVolumeControl() {
      this.showVolumeControl = false
    },
    toggleSpeedOptions() {
      this.showSpeedOptions = !this.showSpeedOptions
      if (this.showSpeedOptions) {
        this.showModeOptions = false
        this.showVolumeControl = false
        this.showFontSizeControl = false
      }
    },
    hideSpeedOptions() {
      this.showSpeedOptions = false
    },
    toggleModeOptions() {
      this.showModeOptions = !this.showModeOptions
      if (this.showModeOptions) {
        this.showSpeedOptions = false
        this.showVolumeControl = false
        this.showFontSizeControl = false
      }
    },
    hideModeOptions() {
      this.showModeOptions = false
    },
    onSetPlaybackRate(rate) {
      this.setPlaybackRate(rate)
      this.showSpeedOptions = false
    },
    setPlayMode(mode) {
      this.playMode = mode
      this.hideModeOptions()
      if (this.sound) {
        this.pauseCurrent?.()
        this.isPlaying = false
      }
    },
    toggleSubtitles() {
      this.isSubtitlesVisible = !this.isSubtitlesVisible
    },
    toggleFontSizeControl() {
      this.showFontSizeControl = !this.showFontSizeControl
      if (this.showFontSizeControl) {
        this.showVolumeControl = false
        this.showSpeedOptions = false
        this.showModeOptions = false
      }
    },
    hideFontSizeControl() {
      this.showFontSizeControl = false
    },
    setFontSize(value) {
      this.fontSize = clampSubtitleFontSize(value)
      window.localStorage.setItem(getSubtitleFontSizeStorageKey(), String(this.fontSize))
    },
    handleKeydown(event) {
      if (['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT'].includes(event.target.tagName) || event.target.isContentEditable) return
      if (window.currentAudioPlayer !== this.instanceId) return
      if (!this.sound) return
      if (event.repeat) return

      switch (event.code) {
        case 'Space':
          event.preventDefault()
          this.togglePlay()
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          this.playPreviousSubtitle()
          break
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault()
          this.playNextSubtitle()
          break
        case 'Enter':
        case 'NumpadEnter':
          if (!this.isRecording) break
          event.preventDefault()
          this.finishSpeaking()
          break
      }
    },
    async onTranslateLanguage(lang) {
      if (this.videoSrc && this.isDemo && !['zh', 'en', 'none'].includes(lang)) return
      const result = await this.handleTranslateLanguage(lang, {
        audioId: this.audioId,
        materialId: this.materialId,
        materialBook: this.materialBook,
        splitType: this.splitType,
        isDemo: this.isDemo,
        demoTranslations: this.demoTranslations,
        demoSourceSubtitles: this.displaySubtitles,
      })
      if (result?.success === false) {
        ElMessage.error(result.error?.message || this.$t('audioPlayer.translationError'))
      }
    },
    handleSplitTypeChange(type) {
      if (this.videoSrc && this.isDemo && !['practice', 'period'].includes(type)) return
      this.splitType = type
      if (type === 'none') {
        this.useSmartSplit = false
      } else {
        this.useSmartSplit = true
      }
      if (this.isTranslated && this.targetLanguage) {
        this.onTranslateLanguage(this.targetLanguage)
      }
    },
  },
}
</script>

<style scoped>
@import '../../styles/audio-player.css';
@import '../../styles/player-theme.css';

.player-container {
  position: relative;
}

.audio-player-wrapper {
  transition: padding-right var(--duration-panel) var(--ease-spring);
}

.audio-player-wrapper.has-video {
  padding-left: 0;
}

.audio-player-wrapper.has-video .player-container {
  position: relative;
  max-width: none;
}

.generation-dialog-intro {
  margin: var(--space-1) 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  font-weight: 400;
  line-height: 1.5;
}

.player-container :deep(.dialog-overlay--generation) {
  padding: var(--space-5);
  background: color-mix(in srgb, var(--color-overlay-strong) 58%, transparent);
  backdrop-filter: blur(8px);
}

.player-container :deep(.dialog--generation) {
  width: min(100%, 580px);
  max-width: 580px;
  border-color: var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
}

.player-container :deep(.dialog--generation .dialog-header) {
  align-items: flex-start;
  padding: var(--space-6) var(--space-6) var(--space-5);
  border-bottom: 0;
}

.player-container :deep(.dialog--generation .dialog-heading) {
  align-items: flex-start;
  gap: var(--space-3);
}

.player-container :deep(.dialog--generation .dialog-header h3) {
  color: var(--ink-primary);
  font-size: var(--font-size-xl);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.25;
}

.player-container :deep(.dialog--generation .dialog-content) {
  padding: 0 var(--space-6) var(--space-5);
}

.player-container :deep(.dialog--generation .dialog-buttons) {
  gap: var(--space-2);
  margin: 0;
  padding: var(--space-4) var(--space-6) var(--space-5);
  background: var(--color-surface);
  border-top-color: var(--border-subtle);
}

.generation-dialog-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  color: var(--signal-active-strong);
  background: var(--surface-active);
  border: 1px solid var(--border-active);
  border-radius: var(--radius-md);
}

.generation-dialog-mark i {
  display: block;
  width: 2px;
  background: currentColor;
  border-radius: var(--radius-pill);
}

.generation-dialog-mark i:nth-child(1),
.generation-dialog-mark i:nth-child(4) {
  height: 9px;
}

.generation-dialog-mark i:nth-child(2) {
  height: 17px;
}

.generation-dialog-mark i:nth-child(3) {
  height: 13px;
}

.generation-settings-panel {
  padding: var(--space-4);
  background: var(--surface-subtle);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
}

.generation-settings {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
}

.generation-field {
  display: grid;
  gap: var(--space-2);
}

.generation-field-label {
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  font-weight: 600;
  letter-spacing: 0.02em;
}

.player-container :deep(.dialog) {
  overflow: visible;
}

.player-container :deep(.dialog--generation .option-select-control) {
  min-height: 48px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border-color: var(--border-default);
  box-shadow: var(--shadow-sm);
}

.player-container :deep(.dialog--generation .option-select-control:hover),
.player-container :deep(.dialog--generation .option-select.open .option-select-control) {
  background: var(--color-surface);
  border-color: var(--border-active);
}

.player-container :deep(.dialog--generation .option-select-badge) {
  min-width: 26px;
  height: 26px;
  background: var(--surface-active);
  border-color: var(--border-active);
}

.player-container :deep(.dialog--generation .option-select-menu) {
  margin-top: 2px;
  padding: var(--space-2);
  border-color: var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-float);
}

.generation-usage {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
  margin-top: var(--space-3);
  padding: var(--space-3) var(--space-1) 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
  font-variant-numeric: tabular-nums;
}

.generation-usage-item:last-child {
  color: var(--ink-tertiary);
}

.generation-cancel-btn,
.generation-confirm-btn,
.next-lesson-start {
  min-width: 92px;
  min-height: 42px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.generation-cancel-btn {
  color: var(--ink-secondary);
  background: var(--color-surface);
  border: 1px solid var(--border-default);
}

.generation-confirm-btn {
  color: var(--color-text-inverse);
  background: var(--ink-primary);
  border: 1px solid var(--ink-primary);
}

.generation-cancel-btn:hover {
  color: var(--ink-primary);
  background: var(--surface-subtle);
  border-color: var(--color-border-strong);
}

.generation-confirm-btn:hover {
  background: color-mix(in srgb, var(--ink-primary) 88%, white);
  border-color: color-mix(in srgb, var(--ink-primary) 88%, white);
  box-shadow: var(--shadow-control);
  transform: translateY(-1px);
}

.next-lesson-start {
  color: var(--color-text-inverse);
  background: var(--color-brand-600);
  border: 1px solid var(--color-brand-600);
}

.next-lesson-start:hover {
  background: color-mix(in srgb, var(--color-brand-600) 88%, black);
  border-color: color-mix(in srgb, var(--color-brand-600) 88%, black);
  box-shadow: var(--shadow-control);
  transform: translateY(-1px);
}

.next-lesson-intro {
  margin: 0 0 var(--space-4);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.usage-paywall-options {
  display: grid;
  gap: var(--space-2);
  margin: 0;
  padding-left: var(--space-5);
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.next-lesson-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3) var(--space-4);
  background: var(--surface-subtle);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
}

.next-lesson-name {
  color: var(--ink-primary);
  font-weight: 600;
  line-height: 1.5;
}

.next-lesson-meta {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.generation-cancel-btn:focus-visible,
.generation-confirm-btn:focus-visible,
.next-lesson-start:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand);
}

@media (max-width: 768px) {
  .player-container :deep(.dialog-overlay--generation) {
    align-items: flex-end;
    padding: var(--space-3);
  }

  .player-container :deep(.dialog--generation) {
    width: 100%;
  }

  .player-container :deep(.dialog--generation .dialog-header) {
    padding: var(--space-5) var(--space-4) var(--space-4);
  }

  .player-container :deep(.dialog--generation .dialog-content) {
    padding: 0 var(--space-4) var(--space-4);
  }

  .player-container :deep(.dialog--generation .dialog-buttons) {
    padding: var(--space-3) var(--space-4) var(--space-4);
  }

  .generation-settings {
    grid-template-columns: 1fr;
  }

  .generation-usage {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--space-1);
  }

  .generation-cancel-btn,
  .generation-confirm-btn,
  .next-lesson-start {
    flex: 1;
  }

  .audio-player-wrapper.has-video {
    padding: 0;
  }

  .audio-player-wrapper.has-video .player-container {
    height: 100dvh;
    border-radius: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .generation-cancel-btn,
  .generation-confirm-btn,
  .next-lesson-start {
    transition: none;
  }
}

@media (max-height: 540px) and (orientation: landscape) {
  .audio-player-wrapper.has-video {
    padding: calc(var(--navbar-height) + 0.25rem) 0.35rem 0.25rem;
  }

  .audio-player-wrapper.has-video .player-container {
    height: calc(100dvh - var(--navbar-height) - 0.5rem);
  }

  .audio-player-wrapper.has-video :deep(.top-bar) {
    min-height: 48px;
    height: 48px;
    padding: 0.25rem 0.6rem;
  }

  .audio-player-wrapper.has-video :deep(.title-eyebrow) {
    display: none;
  }

}
</style>
